import React, { useEffect, useState } from 'react';
import { FuegoProvider } from '@nandorojo/swr-firestore';
import { BrowserRouter } from 'react-router-dom';
import Div100vh from 'react-div-100vh';
import { LaptopOutlined } from '@ant-design/icons';
import { Result, Typography } from 'antd';
import { FormattedMessage } from 'react-intl';

import { Auth } from 'components/utils/Auth';
import { IntlConfig } from 'components/utils/IntlConfig';
import { Routing } from 'components/utils/Routing';
import { fuego } from 'services/firebase/fuego';

import packageJson from '../package.json';

import './App.less';

const VersionFooter = () => (
  <div className="version-footer">Votim {packageJson.version}</div>
);

const MOBILE_MEDIA_QUERY = '(max-width: 1024px) and (pointer: coarse)';

const detectIsMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
};

const MobileBlockedScreen = () => (
  <div className="mobile-blocked-screen">
    <Result
      icon={<LaptopOutlined />}
      title={
        <FormattedMessage
          id="mobileBlocked.title"
          defaultMessage="Veuillez vous connecter depuis votre ordinateur."
        />
      }
      subTitle={
        <div className="mobile-blocked-screen__subtitle">
          <Typography.Paragraph style={{ marginBottom: 8 }}>
            <FormattedMessage
              id="mobileBlocked.subtitle"
              defaultMessage="Votim n'est pas disponible sur mobile ou tablette."
            />
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            <FormattedMessage
              id="mobileBlocked.hint"
              defaultMessage="Ouvrez cette page depuis un navigateur desktop pour continuer."
            />
          </Typography.Paragraph>
        </div>
      }
    />
  </div>
);

const App: React.FunctionComponent = () => {
  const [isMobileDevice, setIsMobileDevice] = useState<boolean>(() =>
    detectIsMobileDevice(),
  );

  useEffect(() => {
    // Clear session storage on page unload or reload to force users back to the homepage on re-entry.
    // This ensures a user gesture is required, resolving issues like browsers blocking the AudioContext from starting automatically.
    const handleBeforeUnload = () => {
      sessionStorage.clear();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleChange = () => setIsMobileDevice(mediaQueryList.matches);

    handleChange();
    mediaQueryList.addEventListener('change', handleChange);
    return () => {
      mediaQueryList.removeEventListener('change', handleChange);
    };
  }, []);

  return (
    <BrowserRouter>
      <IntlConfig>
        {isMobileDevice ? (
          <Div100vh>
            <MobileBlockedScreen />
            <VersionFooter />
          </Div100vh>
        ) : (
          <FuegoProvider fuego={fuego}>
            <Auth>
              <Div100vh>
                <Routing />
                <VersionFooter />
              </Div100vh>
            </Auth>
          </FuegoProvider>
        )}
      </IntlConfig>
    </BrowserRouter>
  );
};

export default App;
